import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { BarcodeLookup, Can, PublicBarcodeLookup } from '../../shared/api';
import { parseEan } from '../../shared/barcode';
import { countryLongName } from '../../shared/countries';
import { ApiError, lookupPublicBarcode } from '../api';
import { CanImage } from '../components/CanImage';
import { Sheet } from '../components/Sheet';
import { sizeLabel } from '../format';
import { Scanner } from './Scanner';

type ScanResult = { ean: string; data: PublicBarcodeLookup | null; error: string | null; unknown: boolean };

/** Each lookup is a fresh snapshot. Closing or replacing it invalidates its response. */
export function usePublicScan(slug: string) {
  const [result, setResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const request = useRef<AbortController | null>(null);
  const current = useRef<ScanResult | null>(null);
  const active = useRef(false);
  const locked = useRef(false);
  const clear = () => {
    request.current?.abort();
    request.current = null;
    current.current = null;
    locked.current = false;
    setLoading(false);
    setResult(null);
  };
  useEffect(
    () => () => {
      request.current?.abort();
      active.current = false;
    },
    [slug],
  );
  const lookup = async (ean: string, refresh = false) => {
    if (!active.current || (locked.current && !refresh)) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    locked.current = true;
    setLoading(true);
    const next: ScanResult = {
      ean,
      data: refresh ? (current.current?.data ?? null) : null,
      error: null,
      unknown: false,
    };
    current.current = next;
    setResult(next);
    try {
      next.data = await lookupPublicBarcode(slug, ean, controller.signal);
    } catch (err) {
      if (controller.signal.aborted) return;
      next.data = null;
      next.unknown = err instanceof ApiError && err.body.code === 'unknown-barcode';
      next.error = next.unknown
        ? null
        : err instanceof ApiError && err.status === 429
          ? 'Je hebt veel codes achter elkaar opgezocht. Wacht even en probeer opnieuw.'
          : err instanceof ApiError && err.body.code === 'list-not-found'
            ? 'Deze zoeklijst bestaat niet meer. Ga terug naar de lijst om de link te controleren.'
            : 'De code kon niet worden opgezocht. Controleer je verbinding en probeer opnieuw.';
    }
    if (controller.signal.aborted || request.current !== controller) return;
    current.current = { ...next };
    setResult(current.current);
    setLoading(false);
  };
  return {
    result,
    loading,
    resetKey,
    start: () => {
      clear();
      active.current = true;
    },
    stop: () => {
      active.current = false;
      clear();
    },
    next: () => {
      clear();
      setResetKey((key) => key + 1);
    },
    dismiss: clear,
    lookup,
    refresh: async () => {
      if (active.current && current.current) await lookup(current.current.ean, true);
    },
  };
}

export function ScanCan({ can, barcode }: { can: Can; barcode: BarcodeLookup }) {
  return (
    <div className="scan-can">
      <div className="scan-can__image">
        <CanImage imageId={can.imageId} alt={can.name} />
      </div>
      <div className="scan-can__body">
        <h3 className="sheet__name">{can.name}</h3>
        <div className="scan-can__variant">{foundVariant(barcode)}</div>
        <div className="scan-can__ean">EAN {barcode.ean}</div>
      </div>
    </div>
  );
}

export function foundVariant(barcode: Pick<BarcodeLookup, 'country' | 'sizeMl'>) {
  return [
    barcode.country ? countryLongName(barcode.country) : 'Land onbekend',
    barcode.sizeMl ? sizeLabel(barcode.sizeMl) : 'Inhoud onbekend',
  ].join(' · ');
}

export function wantedVariant(barcode: Pick<BarcodeLookup, 'country' | 'sizeMl'>) {
  return [
    barcode.country ? countryLongName(barcode.country) : 'Elk land',
    barcode.sizeMl ? sizeLabel(barcode.sizeMl) : 'Elke inhoud',
  ].join(' · ');
}

export function PublicScan({
  owner,
  scan,
  hidden,
  onBack,
  onDetail,
  reservation,
}: {
  owner: string;
  scan: ReturnType<typeof usePublicScan>;
  hidden: boolean;
  onBack: () => void;
  onDetail: (result: PublicBarcodeLookup) => void;
  reservation: (result: PublicBarcodeLookup) => ReactNode;
}) {
  const [code, setCode] = useState('');
  const [validation, setValidation] = useState('');
  const [cameraKey, setCameraKey] = useState(0);
  const { result, loading } = scan;
  const data = result?.data;
  const title = loading
    ? 'Streepjescode opzoeken…'
    : data
      ? {
          match: `${owner} zoekt deze!`,
          'not-wanted': `Niet op ${owner}s zoeklijst`,
          'wrong-variant': 'Zelfde smaak, andere variant',
          incomplete: 'Variant nog controleren',
        }[data.status]
      : result?.unknown
        ? 'Onbekende streepjescode'
        : 'Opzoeken mislukt';

  return (
    <div className="public-scan">
      <button type="button" className="pill public-scan__back" onClick={onBack}>
        ← Terug naar {owner}s lijst
      </button>
      <div className="label label--green">In de winkel of op vakantie?</div>
      <h1 className="friend__hero">
        Zoekt {owner}
        <br />
        <span>dit blik?</span>
      </h1>
      <p className="friend__intro">Scan de streepjescode en vergelijk het blik met de zoeklijst.</p>
      <Scanner
        key={cameraKey}
        onDetected={(ean) => void scan.lookup(ean)}
        paused={!!result || hidden}
        resetKey={scan.resetKey}
        fallback="Vul hieronder de code onder de streepjes in."
      />
      <button type="button" className="text-link" onClick={() => setCameraKey((key) => key + 1)}>
        Camera opnieuw starten
      </button>
      <form
        className="public-scan__manual"
        onSubmit={(event) => {
          event.preventDefault();
          const ean = parseEan(code);
          if (!ean) {
            setValidation('Vul een streepjescode van 8, 12, 13 of 14 cijfers in.');
            return;
          }
          setValidation('');
          void scan.lookup(ean);
        }}
      >
        <label className="label" htmlFor="public-ean">
          Of vul de code in
        </label>
        <input
          id="public-ean"
          className="input input--mono"
          inputMode="numeric"
          autoComplete="off"
          placeholder="EAN onder de streepjescode"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          aria-describedby={validation ? 'ean-validation' : undefined}
        />
        {validation && (
          <div id="ean-validation" className="error" role="alert">
            {validation}
          </div>
        )}
        <button className="btn btn--outline" type="submit">
          Controleer code
        </button>
      </form>
      {result && !hidden && (
        <Sheet label={title} className="scan-result" onClose={scan.dismiss}>
          {(close) => (
            <>
              <div className={`label${data?.status === 'match' ? ' label--green' : ''}`}>Scanresultaat</div>
              <h2 className="sheet__name" aria-live="polite">
                {title}
              </h2>
              {data && <ScanCan can={data.can} barcode={data.barcode} />}
              {!data && <div className="scan-can__ean">EAN {result.ean}</div>}
              {loading ? (
                <p className="note" role="status">
                  We controleren de actuele zoeklijst.
                </p>
              ) : (
                <>
                  {data?.status === 'match' && reservation(data)}
                  {data?.status === 'not-wanted' && (
                    <p className="note">Deze smaak staat niet op de huidige zoeklijst.</p>
                  )}
                  {data && (data.status === 'wrong-variant' || data.status === 'incomplete') && data.wanted && (
                    <>
                      <p className="note">
                        <strong>{owner} zoekt:</strong>
                        <br />
                        {wantedVariant(data.wanted)}
                        <br />
                        {data.status === 'incomplete'
                          ? 'Niet alle variantgegevens zijn bekend. Vergelijk ze met het blik.'
                          : 'Het gescande blik wijkt af van deze voorkeur.'}
                      </p>
                      <button type="button" className="btn" onClick={() => onDetail(data)}>
                        Bekijk de gezochte variant
                      </button>
                    </>
                  )}
                  {result.unknown && (
                    <p className="note">
                      We kennen deze code nog niet. Vergelijk zelf de smaak en variant met {owner}s lijst.
                    </p>
                  )}
                  {result.error && (
                    <>
                      <p className="error" role="alert">
                        {result.error}
                      </p>
                      <button type="button" className="btn" onClick={() => void scan.lookup(result.ean, true)}>
                        Opnieuw proberen
                      </button>
                    </>
                  )}
                </>
              )}
              <div className="scan-actions">
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => {
                    scan.next();
                  }}
                >
                  Volgend blik scannen
                </button>
                <button type="button" className="text-link" onClick={onBack}>
                  Bekijk {owner}s lijst
                </button>
                <button type="button" className="text-link" onClick={close}>
                  Sluiten
                </button>
              </div>
            </>
          )}
        </Sheet>
      )}
    </div>
  );
}
